import React, { useState, useEffect, useRef } from 'react'
import TextInput from '../components/TextInput'
import MultilineInput from '../components/MultilineInput'
import { generateFilledPdf } from '../utils/pdfHandler'
import Button from '../components/Button'
import Colors from '../colors'
import Label from '../components/Label'
import Title from '../components/Title'
import { debounce } from 'lodash';
import { Snackbar, Alert } from '@mui/material';
import { Download } from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom';
import { deleteDocByCollectionAndId, getDocByCollectionAndId, updateDocByCollectionAndId } from '../firebase'
import ConfirmationModal from '../components/modals/ConfirmationModal'

function CreateIncident({ user }) {
  const navigate = useNavigate();
  const { incidentId } = useParams();
  //Assume the form is locked
  const [isArchived, setIsArchived] = useState(true)

  //Last saved
  const [lastUpdated, setLastUpdated] = useState()

  //FormData state - object containing all form data
  const [formData, setFormData] = useState({})

  const updateField = (fieldName, newValue) => {
    setFormData({ ...formData, [fieldName]: newValue })
  }

  const handleSave = async () => {
    await updateDocByCollectionAndId('Incidents', incidentId, formData)
  }

  const handleReturn = () => {
    handleSave();
    navigate(-1);
  }

  const handleDelete = () => {
    deleteDocByCollectionAndId('Incidents', incidentId)
    navigate(-1);
  }

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const initialRender = useRef(true)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [archivedSnackbarOpen, setArchivedSnackbarOpen] = useState(false)

  useEffect(() => {
    if (!user) return;
    window.scrollTo(0, 0);
    const fetchIncidentAndForm = async () => {
      const incident = await getDocByCollectionAndId('Incidents', incidentId)
      setFormData(incident)
      setLastUpdated(incident.lastUpdated.toDate())
      const form = await getDocByCollectionAndId('Forms', incident.formId)
      setIsArchived(form.isArchived || form.userId !== user.uid)
      if (form.isArchived || form.userId !== user.uid) setArchivedSnackbarOpen(true)
    }
    fetchIncidentAndForm()
  }, [incidentId, user])

  useEffect(() => {
    if (isArchived) return;
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    const saveForm = debounce(() => {
      setSnackbarOpen(true);
      const newLastUpdated = new Date()
      setLastUpdated(newLastUpdated);
      formData.lastUpdated = newLastUpdated;
      updateDocByCollectionAndId('Incidents', incidentId, formData)
    }, 3000);
    saveForm();

    // Clean-up function to cancel the debounce timer
    return () => {
      saveForm.cancel();
    };
  }, [formData, incidentId, isArchived])

  const handleDownload = () => {
    const textData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      middleInitial: formData.middleInitial,
      studentNumber: formData.studentNumber,
      address: formData.address,
      postalCode: formData.postalCode,
      city: formData.city,
      province: formData.province,
      phoneNumber: formData.phoneNumber,
      schoolDept: formData.schoolDept,
      incidentLocationDescription: formData.incidentLocationDescription,
      incidentDescription: formData.incidentDescription,
      injuryDescription: formData.injuryDescription,
      personReportedTo: formData.personReportedTo,
      supervisorName: formData.supervisorName,
      preventedDescription: formData.preventedDescription,
      explainWasAwarePain: formData.explainWasAwarePain,
      additionalInfo: formData.additionalInfo,
    }

    const booleanData = {
      wasEquipmentUsed: formData.wasEquipmentUsed,
      goToClinic: formData.goToClinic,
      wereWitnesses: formData.wereWitnesses,
      firstAidReceived: formData.firstAidReceived,
      wasAwarePain: formData.wasAwarePain,
    }

    const dateData = {
      birthDate: formData.birthDate,
    }

    const dateTimeData = {
      incidentDateTime: formData.incidentDateTime,
      reportedDateTime: formData.reportedDateTime,
    }

    if (formData.wasEquipmentUsed) textData.equipmentUsed = formData.equipmentUsed
    if (formData.wereWitnesses) textData.witnesses = formData.witnesses
    if (formData.firstAidReceived) {
      dateData.firstAidDate = formData.firstAidDate
      textData.firstAidAttendantName = formData.firstAidAttendantName
    }
    if (formData.goToClinic) {
      dateData.clinicDate = formData.clinicDate
      textData.phisicianName = formData.phisicianName
    }

    generateFilledPdf(textData, booleanData, dateData, dateTimeData)
  }

  const radioContainerStyles = {
    marginTop: '20px',
    marginBottom: '20px'
  }

  const radioGroupStyles = {
    display: 'flex',
    gap: '20px',
  }

  const radioLabelStyles = {
    display: 'flex',
    alignItems: 'center'
  }
  const radioStyles = {
    width: '1.5rem',
    height: '1.1rem',
  }

  const buttonContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    marginTop: '45px'
  }


  return (
    <div>
      <Snackbar open={snackbarOpen} autoHideDuration={1500} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} sx={{ marginTop: '50px' }}>
        <Alert severity="success" sx={{ width: '100%' }}>
          Saved!
        </Alert>
      </Snackbar>
      <Snackbar open={archivedSnackbarOpen} autoHideDuration={10000} onClose={() => setArchivedSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ marginTop: '50px' }}>
        <Alert severity="info" sx={{ width: '100%' }}>
          This Incident is archived or you do not have permission to edit it.
        </Alert>
      </Snackbar>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <Title>Student Injury Report</Title>
        <span style={{ display: 'block', marginTop: '-15px' }}>Last updated at {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '...'} </span>
      </div>
      <TextInput label={'First name'} setText={(newVal) => updateField('firstName', newVal)} name={'firstName'} disabled={isArchived} value={formData.firstName} />
      <TextInput label={'last name'} setText={(newVal) => updateField('lastName', newVal)} name={'lastName'} disabled={isArchived} value={formData.lastName} />
      <TextInput label={'Middle Initial'} setText={(newVal) => updateField('middleInitial', newVal)} type='text' name={'midddleInitial'} disabled={isArchived} value={formData.middleInitial} />
      <TextInput label={'Student Number'} setText={(newVal) => updateField('studentNumber', newVal)} type='text' name={'studentNumber'} disabled={isArchived} value={formData.studentNumber} />
      <TextInput label={'Address'} setText={(newVal) => updateField('address', newVal)} type='text' name={'address'} disabled={isArchived} value={formData.address} />
      <TextInput label={'Postal Code'} setText={(newVal) => updateField('postalCode', newVal)} type='text' name={'postalCode'} disabled={isArchived} value={formData.postalCode} />
      <TextInput label={'City'} setText={(newVal) => updateField('city', newVal)} type='text' name={'city'} disabled={isArchived} value={formData.city} />
      <TextInput label={'Province'} setText={(newVal) => updateField('province', newVal)} type='text' name={'province'} disabled={isArchived} value={formData.province} />
      <TextInput label={'Home/Cell Phone Number'} setText={(newVal) => updateField('phoneNumber', newVal)} type='phone' name={'homeCellNumber'} disabled={isArchived} value={formData.phoneNumber} />
      <TextInput label="School/Program/Department" setText={(newVal) => updateField('schoolDept', newVal)} type='text' name={'schoolDept'} disabled={isArchived} value={formData.schoolDept} />
      <TextInput label={'Birth Date'} setText={(newVal) => updateField('birthDate', newVal)} type='date' max="9999-12-31" name={'birthDate'} disabled={isArchived} value={formData.birthDate} />
      <TextInput label="Incident Date / Time" setText={(newVal) => updateField('incidentDateTime', newVal)} type='datetime-local' max="9999-12-31T23:59" name={'incidentDateTime'} disabled={isArchived} value={formData.incidentDateTime} />
      <TextInput label={'Reported Date / Time'} setText={(newVal) => updateField('reportedDateTime', newVal)} type='datetime-local' max="9999-12-31T23:59" name={'reportedDateTime'} disabled={isArchived} value={formData.reportedDateTime} />
      <TextInput label={'Person Reported To'} setText={(newVal) => updateField('personReportedTo', newVal)} type='text' name={'personReportedTo'} disabled={isArchived} value={formData.personReportedTo} />
      <TextInput label={'Supervisor Name'} setText={(newVal) => updateField('supervisorName', newVal)} type='text' name={'supervisorName'} disabled={isArchived} value={formData.supervisorName} />
      <MultilineInput label={'Describe the Incident Location/Address (e.g. Hospital, shop, lunchroom, parking lot)'} setText={(newVal) => updateField('incidentLocationDescription', newVal)} name={'incidentLocationDescription'} disabled={isArchived} value={formData.incidentLocationDescription} />
      <MultilineInput label={'Describe the incident'} setText={(newVal) => updateField('incidentDescription', newVal)} name={'incidentDescription'} disabled={isArchived} value={formData.incidentDescription} />
      <MultilineInput label={'Describe the injury (what part of the body)'} setText={(newVal) => updateField('injuryDescription', newVal)} name={'injuryDescription'} disabled={isArchived} value={formData.injuryDescription} />
      <MultilineInput label={'In your Opinion is there anyway this incident could have been prevented?'} setText={(newVal) => updateField('preventedDescription', newVal)} name={'preventedDescription'} disabled={isArchived} value={formData.preventedDescription} />

      <div style={radioContainerStyles}>
        <Label>Was protective equipment used?</Label>
        <div style={radioGroupStyles}>
          <div style={radioLabelStyles}>
            <input type='radio' id='yesBtnWasEquipmentUsed' disabled={isArchived} value='yes' name='wasEquipmentUsed' style={radioStyles} checked={formData.wasEquipmentUsed === true} onChange={() => updateField('wasEquipmentUsed', true)} />
            <label htmlFor='yesBtnWasEquipmentUsed'>Yes</label>
          </div>
          <div style={radioLabelStyles}>
            <input type='radio' id='noBtnWasEquipmentUsed' disabled={isArchived} value='no' name='wasEquipmentUsed' style={radioStyles} checked={formData.wasEquipmentUsed === false} onChange={() => updateField('wasEquipmentUsed', false)} />
            <label htmlFor='noBtnWasEquipmentUsed'>No</label>
          </div>
        </div>
      </div>
      {formData.wasEquipmentUsed === true &&
        <TextInput label={'Equipment Used'} setText={(newVal) => updateField('equipmentUsed', newVal)} type='text' name={'equipmentUsed'} disabled={isArchived} value={formData.equipmentUsed} />
      }

      <div style={radioContainerStyles}>
        <Label>Were there witnesses?</Label>
        <div style={radioGroupStyles}>
          <div style={radioLabelStyles}>
            <input type='radio' id='yesBtnWereWitnesses' disabled={isArchived} value='yes' name='wereWitnesses' style={radioStyles} checked={formData.wereWitnesses === true} onChange={() => updateField('wereWitnesses', true)} />
            <label htmlFor='yesBtnWereWitnesses'>Yes</label>
          </div>
          <div style={radioLabelStyles}>
            <input type='radio' id='noBtnWereWitnesses' disabled={isArchived} value='no' name='WereWitnesses' style={radioStyles} checked={formData.wereWitnesses === false} onChange={() => updateField('wereWitnesses', false)} />
            <label htmlFor='noBtnWereWitnesses'>No</label>
          </div>
        </div>
      </div>
      {formData.wereWitnesses === true &&
        <TextInput label={'Witnesses'} setText={(newVal) => updateField('witnesses', newVal)} type='text' name={'witnesses'} disabled={isArchived} value={formData.witnesses} />
      }

      <div style={radioContainerStyles}>
        <Label>Did you receive first aid?</Label>
        <div style={radioGroupStyles}>
          <div style={radioLabelStyles}>
            <input type='radio' id='yesBtnFirstAid' disabled={isArchived} value='yes' name='firstAidReceived' style={radioStyles} checked={formData.firstAidReceived === true} onChange={() => updateField('firstAidReceived', true)} />
            <label htmlFor='yesBtnFirstAid'>Yes</label>
          </div>
          <div style={radioLabelStyles}>
            <input type='radio' id='noBtnFirstAid' disabled={isArchived} value='no' name='firstAidReceived' style={radioStyles} checked={formData.firstAidReceived === false} onChange={() => updateField('firstAidReceived', false)} />
            <label htmlFor='noBtnFirstAid'>No</label>
          </div>
        </div>
      </div>
      {formData.firstAidReceived === true &&
        <>
          <TextInput label={'First Aid Date'} setText={(newVal) => updateField('firstAidDate', newVal)} type='date' max="9999-12-31" name={'firstAidDate'} disabled={isArchived} value={formData.firstAidDate} />
          <TextInput label={'First Aid Attendant Name'} setText={(newVal) => updateField('firstAidAttendantName', newVal)} type='text' name={'firstAidAttendantName'} disabled={isArchived} value={formData.firstAidAttendantName} />
        </>
      }

      <div style={radioContainerStyles}>
        <Label>Did you go to a hospital, clinic, or see a physician?</Label>
        <div style={radioGroupStyles}>
          <div style={radioLabelStyles}>
            <input type='radio' id='yesBtnGoToClinic' disabled={isArchived} value='yes' name='GoToClinic' style={radioStyles} checked={formData.goToClinic === true} onChange={() => updateField('goToClinic', true)} />
            <label htmlFor='yesBtnGoToClinic'>Yes</label>
          </div>
          <div style={radioLabelStyles}>
            <input type='radio' id='noBtnGoToClinic' disabled={isArchived} value='no' name='GoToClinic' style={radioStyles} checked={formData.goToClinic === false} onChange={() => updateField('goToClinic', false)} />
            <label htmlFor='noBtnGoToClinic'>No</label>
          </div>
        </div>
      </div>
      {formData.goToClinic === true &&
        <>
          <TextInput label={'Clinic Date'} setText={(newVal) => updateField('clinicDate', newVal)} type='date' max="9999-12-31" name={'clinicDate'} disabled={isArchived} value={formData.clinicDate} />
          <TextInput label={'Name of Physician or provider'} setText={(newVal) => updateField('phisicianName', newVal)} type='text' name={'phisicianName'} disabled={isArchived} value={formData.phisicianName} />
        </>
      }

      <div style={radioContainerStyles}>
        <Label>Before this incident were you aware of any recent pain or disability in the area of your reported injury?</Label>
        <div style={radioGroupStyles}>
          <div style={radioLabelStyles}>
            <input type='radio' id='yesBtnAwarePain' disabled={isArchived} value='yes' name='wasAwarePain' style={radioStyles} checked={formData.wasAwarePain === true} onChange={() => updateField('wasAwarePain', true)} />
            <label htmlFor='yesBtnAwarePain'>Yes</label>
          </div>
          <div style={radioLabelStyles}>
            <input type='radio' id='noBtnAwarePain' disabled={isArchived} value='no' name='wasAwarePain' style={radioStyles} checked={formData.wasAwarePain === false} onChange={() => updateField('wasAwarePain', false)} />
            <label htmlFor='noBtnAwarePain'>No</label>
          </div>
        </div>
      </div>
      <MultilineInput label={'Explain'} setText={(newVal) => updateField('explainWasAwarePain', newVal)} disabled={isArchived} value={formData.explainWasAwarePain} name={'explainWasAwarePain'} />

      <MultilineInput label={'Additional Info'} setText={(newVal) => updateField('additionalInfo', newVal)} name={'additionalInfo'} disabled={isArchived} value={formData.additionalInfo} />
      <div style={buttonContainerStyles} >
        {!isArchived &&
          <Button color={Colors.green} onClick={handleReturn}>Save and Return</Button>
        }
        <Button color={Colors.blue} onClick={handleDownload}><Download sx={{ color: 'white', marginLeft: '-15px' }} />&nbsp;Download PDF</Button>
        {!isArchived &&
          <Button color={Colors.red} onClick={() => setDeleteModalOpen(true)}>Delete</Button>
        }
      </div>

      <ConfirmationModal title={'Are you sure you want to delete this incident?'} mainAction={'Delete'} mainActionHandler={handleDelete} isVisible={deleteModalOpen} setIsVisible={setDeleteModalOpen} />
    </div>
  )
}

export default CreateIncident